import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_MENU_CATEGORIES } from "src/constants/queries";
import { failureToast } from "src/utils/toasts";
import { RootState } from "src/store/index";
import { set_menu_categories, search_items } from "src/store/features/category-slice";
import { MenuItem } from "src/interfaces/menu.interface";

export const useCategoriesWithItems = () => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  
  const { allItems, filteredItems } = useSelector(
    (state: RootState) => state.category
  );

  const fetchCategoriesWithItems = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;

    setIsLoading(true);

    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_MENU_CATEGORIES,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
          activeFilter: "ACTIVE",
        },
      });

      if (data?.data?.categoriesByStoreCorrelationId) {
        dispatch(
          set_menu_categories({
            categories: data.data.categoriesByStoreCorrelationId,
          })
        );
      }
    } catch (err) {
      console.error("Error fetching categories with items:", err);
      const errorMessage = "Failed to fetch categories with items";
      failureToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [axBe, hasRestaurant?.correlationId, dispatch]);

  const searchItems = useCallback(
    (searchTerm: string) => {
      dispatch(search_items(searchTerm));
    },
    [dispatch]
  );

  useEffect(() => {
    // Always fetch when the hook is mounted (when form opens)
    fetchCategoriesWithItems();
  }, [fetchCategoriesWithItems]);

  return {
    allItems: allItems as MenuItem[],
    filteredItems: filteredItems as MenuItem[],
    isLoading,
    fetchCategoriesWithItems,
    searchItems,
  };
};

